import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodProfileContent = ({ onNavigate, userInfo, hodData, isMobile, handleLogout, refreshProfile }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [editedProfile, setEditedProfile] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);





  // Sync hodData to local state
  useEffect(() => {
    if (hodData && hodData.name !== 'Loading...') {
      setProfileData(hodData);
      setEditedProfile(hodData);
      setLoading(false);
    }
  }, [hodData]);



  // Handle profile edit
  const handleEdit = () => {
    setIsEditing(true);
    setSaveMessage(null);
    setSaveError(null);
    console.log('✏️ Entering edit mode');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      console.log('💾 Starting profile save process...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found. Please login again.');
        return;
      }

      // Validate required fields
      if (!editedProfile.name || editedProfile.name.trim() === '') {
        setSaveError('Name is required');
        return;
      }

      // Validate email format if provided
      if (editedProfile.email && editedProfile.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editedProfile.email)) {
          setSaveError('Please enter a valid email address');
          return;
        }
      }

      // Validate phone number if provided
      if (editedProfile.phone && editedProfile.phone.trim() !== '') {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(editedProfile.phone.replace(/\D/g, ''))) {
          setSaveError('Phone number must be exactly 10 digits');
          return;
        }
      }

      // Create FormData for file upload
      const formData = new FormData();

      // Add profile data as JSON string (only editable fields)
      const profileUpdateData = {
        name: editedProfile.name.trim(),
        email: editedProfile.email ? editedProfile.email.trim() : '',
        phone: editedProfile.phone ? editedProfile.phone.replace(/\D/g, '') : '',
        bloodGroup: editedProfile.bloodGroup ? editedProfile.bloodGroup.trim() : '',
        panNumber: editedProfile.panNumber ? editedProfile.panNumber.trim() : '',
        adhaarNumber: editedProfile.adhaarNumber ? editedProfile.adhaarNumber.trim() : ''
      };

      console.log('📤 Profile data to send:', profileUpdateData);

      formData.append('profileData', JSON.stringify(profileUpdateData));

      // Add image file if selected
      if (imageFile) {
        formData.append('profileImage', imageFile);
        console.log('📁 Adding image file to upload:', imageFile.name, 'Size:', imageFile.size);
      }

      console.log('🔄 Sending HOD profile update with FormData');

      const response = await fetch(`${API_LINK}/api/hod/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);

        if (response.status === 401) {
          setSaveError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (response.status === 400) {
          try {
            const errorData = JSON.parse(errorText);
            setSaveError(errorData.message || 'Invalid data provided');
          } catch {
            setSaveError('Invalid data provided');
          }
          return;
        } else if (response.status === 404) {
          setSaveError('HOD profile not found. Please contact support.');
          return;
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Save result:', result);

      if (result.success && result.data) {
        setProfileData(result.data);
        setEditedProfile(result.data);
        setIsEditing(false);
        setImageFile(null);
        setImagePreview(null);
        setSaveMessage('Profile updated successfully!');

        console.log('✅ Profile updated successfully');

        // Clear success message after 5 seconds
        setTimeout(() => setSaveMessage(null), 5000);

        // Refresh global profile data
        if (refreshProfile) {
          refreshProfile();
        }
      } else {
        throw new Error(result.message || 'Failed to update profile');
      }

    } catch (error) {
      console.error('💥 Error saving HOD profile:', error);
      setSaveError(`Failed to save profile: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile(profileData);
    setImageFile(null);
    setImagePreview(null);
    setSaveMessage(null);
    setSaveError(null);
    console.log('❌ Edit cancelled');
  };

  const handleInputChange = (field, value) => {
    // Only allow editing of specific fields for HOD
    const allowedFields = ['name', 'email', 'phone', 'bloodGroup', 'panNumber', 'adhaarNumber'];
    if (!allowedFields.includes(field)) {
      console.warn(`⚠️ Attempt to edit restricted field: ${field}`);
      return;
    }

    console.log(`📝 Updating field ${field} with value:`, value);

    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setSaveError('Image file size must be less than 5MB');
        event.target.value = ''; // Clear the input
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSaveError('Please select a valid image file');
        event.target.value = ''; // Clear the input
        return;
      }

      setImageFile(file);
      setSaveError(null); // Clear any previous errors

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      console.log('📷 Image selected:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Delete profile image from server
  const handleDeleteProfileImage = async () => {
    if (!profileData.profileImage && !imagePreview) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_LINK}/api/hod/profile/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setProfileData(prev => ({ ...prev, profileImage: null }));
        setEditedProfile(prev => ({ ...prev, profileImage: null }));
        handleImageRemove(); // Clear preview and file
        setSaveMessage('Profile image deleted successfully');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setSaveError(errorData.message || 'Failed to delete image');
      }

    } catch (error) {
      console.error('Error deleting HOD profile image:', error);
      setSaveError('Failed to delete profile image');
    }
  };

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'HD';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (imagePreview) {
      return imagePreview; // Use preview for uploaded image
    }
    if (profileData.profileImage) {
      return `${API_LINK}${profileData.profileImage}`;
    }
    return null;
  };

  const ProfileField = ({ label, value, field, type = "text", readonly = false, options = null }) => (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0.5rem' : '1rem',
      alignItems: isMobile ? 'flex-start' : 'center',
      padding: '1rem',
      borderBottom: '1px solid #ecf0f1',
      transition: 'background-color 0.3s ease',
      backgroundColor: readonly ? '#f8f9fa' : 'transparent'
    }}>
      <label style={{
        fontWeight: '600',
        color: '#333',
        minWidth: isMobile ? 'auto' : '200px',
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {label}
        {readonly && (
          <span style={{
            fontSize: '0.7rem',
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            padding: '0.2rem 0.4rem',
            borderRadius: '4px',
            fontWeight: 'normal'
          }}>
            🔒 Protected
          </span>
        )}
      </label>
      {isEditing && !readonly ? (
        options ? (
          <select
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '2px solid #97d6ac',
              borderRadius: '8px',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease',
              backgroundColor: '#fff'
            }}
          >
            <option value="">Select...</option>
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '2px solid #97d6ac',
              borderRadius: '8px',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease',
              backgroundColor: '#fff'
            }}
          />
        )
      ) : (
        <span style={{
          flex: 1,
          color: readonly ? '#6c757d' : '#666',
          fontSize: '0.95rem',
          padding: '0.75rem 0',
          fontStyle: readonly ? 'italic' : 'normal'
        }}>
          {value || 'Not provided'}
        </span>
      )}
    </div>
  );

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading HOD Profile...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load HOD Profile
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Profile Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem',
            flexWrap: 'wrap'
          }}>
            {/* Profile Image with Upload */}
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                fontWeight: 'bold',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                flexShrink: 0,
                overflow: 'hidden',
                backgroundImage: getProfileImageUrl() ? `url(${getProfileImageUrl()})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
                {!getProfileImageUrl() && getUserInitials(profileData.name)}
              </div>

              {/* Image Upload Button */}
              {isEditing && (
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <label style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease'
                  }}>
                    📷
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {(imagePreview || profileData.profileImage) && (
                    <button
                      onClick={imagePreview ? handleImageRemove : handleDeleteProfileImage}
                      style={{
                        background: 'rgba(255, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <h1 style={{
                margin: '0 0 0.5rem 0',
                fontSize: '2.2rem',
                fontWeight: 'bold'
              }}>
                {profileData.name || 'HOD Name'}
              </h1>
              <p style={{
                margin: '0 0 0.5rem 0',
                fontSize: '1.2rem',
                opacity: 0.9
              }}>
                ID: {profileData.hodId || 'HOD ID'}
              </p>
              <p style={{
                margin: 0,
                fontSize: '1rem',
                opacity: 0.8
              }}>
                {profileData.designation || 'Head of Department'} • {profileData.department || 'Department'}
              </p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexShrink: 0
          }}>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ✏️ Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    background: '#ffffff',
                    border: 'none',
                    color: '#238548',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  {isSaving ? '⏳ Saving...' : '✅ Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  ❌ Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {(saveMessage || saveError) && (
        <div style={{
          background: saveMessage ? '#d4edda' : '#f8d7da',
          border: `1px solid ${saveMessage ? '#c3e6cb' : '#f5c6cb'}`,
          color: saveMessage ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>
            {saveMessage ? '✅' : '❌'}
          </span>
          {saveMessage || saveError}
        </div>
      )}

      {/* Profile Information */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '2rem'
      }}>
        {/* Personal Information */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>👤</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Personal Information</h3>
          </div>
          <div>
            <ProfileField label="Full Name" value={profileData.name} field="name" />
            <ProfileField label="Email Address" value={profileData.email} field="email" type="email" />
            <ProfileField label="Phone Number" value={profileData.phone} field="phone" type="tel" />
            <ProfileField
              label="Blood Group"
              value={profileData.bloodGroup}
              field="bloodGroup"
              options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']}
            />
            <ProfileField label="PAN Number" value={profileData.panNumber} field="panNumber" />
            <ProfileField label="Aadhaar Number" value={profileData.adhaarNumber} field="adhaarNumber" />
          </div>
        </div>

        {/* Official Information */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🏢</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Official Information</h3>
          </div>
          <div>
            <ProfileField label="HOD ID" value={profileData.hodId} field="hodId" readonly />
            <ProfileField label="Department" value={profileData.department} field="department" readonly />
            <ProfileField label="Designation" value={profileData.designation} field="designation" readonly />
            <ProfileField label="Academic Qualification" value={profileData.academicQualification} field="academicQualification" readonly />
            <ProfileField label="Batch Name" value={profileData.batchName} field="batchName" readonly />
          </div>
        </div>
      </div>


    </>
  );
};

const HodProfile = (props) => (
  <Layout role="hod" {...props} activePage="hod-profile">
    {(layoutProps) => <HodProfileContent {...props} {...layoutProps} />}
  </Layout>
);

export default HodProfile;